import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";

/**
 * In-house Markdown → JSX renderer (no `react-markdown` / `remark-gfm`
 * dependency). Documents in the support-center KB — and uploaded `.md` files —
 * render through this. It covers the common CommonMark + GFM grammar so a
 * real-world document reads fully formatted:
 *
 *   #..######           → headings (h1–h6)
 *   ```code fence```    → preformatted block (language tag tolerated)
 *   > quote             → blockquote (recursively formatted inside)
 *   | a | b |           → GFM table (with :--: alignment)
 *   - / * / + bullet    → unordered list (NESTED by indent)
 *   1. ordered          → ordered list (NESTED by indent, honours start)
 *   - [ ] / - [x]       → task-list checkboxes
 *   ---                 → thematic break
 *   **bold** ***both*** → strong / strong+em
 *   *em* / _em_         → emphasis
 *   ~~strike~~          → strikethrough
 *   `code`              → inline code
 *   [text](url)         → links (rel=noopener, target=_blank)
 *   ![alt](url)         → images
 *
 * A line that starts with `#` but is NOT a valid 1–6 level heading (a social
 * hashtag like `#XOP`, or a bare `#`) renders as ordinary paragraph text — and
 * is consumed unconditionally, so the block loop can never stall (the
 * historical `#`-led infinite-loop / browser hang). The renderer never trusts
 * arbitrary HTML, so there's no XSS surface. Block/inline elements keep their
 * semantic tags (h1–h6 / p / ul / ol / li / pre / code / a / blockquote /
 * table / del / img) — restyled via MUI `sx` — so tag-based tests hold.
 */
export function Markdown({ source }: { source: string }): JSX.Element {
  return <Box>{parseBlocks(source).map((b, i) => renderBlock(b, i))}</Box>;
}

interface ListItem {
  text: string;
  checked: boolean | null;
  children: ListNode[];
}
interface ListNode {
  ordered: boolean;
  start: number;
  items: ListItem[];
}

type TableAlign = "left" | "center" | "right" | null;

type Block =
  | { kind: "h"; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { kind: "p"; text: string }
  | { kind: "code"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "list"; node: ListNode }
  | { kind: "table"; headers: string[]; rows: string[][]; align: TableAlign[] }
  | { kind: "hr" };

const LIST_RE = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;

function isListLine(line: string): boolean {
  return LIST_RE.test(line);
}

/** A GFM table delimiter row, e.g. `| --- | :--: | ---: |`. */
function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/.test(line) && line.includes("-");
}

/** Split a `| a | b |` table row into trimmed cells (edge pipes optional). */
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  const at = (idx: number): string => lines[idx] ?? "";

  let i = 0;
  // Defensive forward-progress sentinel — if any iteration leaves `i` unmoved
  // (a parser bug), force it forward so the worst case is a dropped line, never
  // an infinite loop / OOM crash on untrusted content.
  let lastIndex = -1;
  while (i < lines.length) {
    if (i === lastIndex) {
      i += 1;
      continue;
    }
    lastIndex = i;
    const line = at(i);

    if (line.trim() === "") {
      i += 1;
      continue;
    }

    // Fenced code block — language token after ``` is tolerated + ignored.
    if (line.trimStart().startsWith("```")) {
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !at(i).trimStart().startsWith("```")) {
        buf.push(at(i));
        i += 1;
      }
      i += 1; // closing fence
      out.push({ kind: "code", text: buf.join("\n") });
      continue;
    }

    if (/^(\*\*\*|---|___)$/.test(line.trim())) {
      out.push({ kind: "hr" });
      i += 1;
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch && headingMatch[1] && headingMatch[2]) {
      out.push({ kind: "h", level: headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6, text: headingMatch[2].trim() });
      i += 1;
      continue;
    }

    // GFM table — a `|`-bearing header line immediately followed by a
    // delimiter row.
    if (line.includes("|") && isTableSeparator(at(i + 1))) {
      const headers = splitTableRow(line);
      const align: TableAlign[] = splitTableRow(at(i + 1)).map((c) => {
        const l = c.startsWith(":");
        const r = c.endsWith(":");
        return l && r ? "center" : r ? "right" : l ? "left" : null;
      });
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && at(i).trim() !== "" && at(i).includes("|")) {
        rows.push(splitTableRow(at(i)));
        i += 1;
      }
      out.push({ kind: "table", headers, rows, align });
      continue;
    }

    // Blockquote — consecutive `>` lines; the inner text is rendered through
    // the full parser so a quote can itself contain lists/headings/etc.
    if (/^\s*>/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>/.test(at(i))) {
        buf.push(at(i).replace(/^\s*>\s?/, ""));
        i += 1;
      }
      out.push({ kind: "quote", text: buf.join("\n") });
      continue;
    }

    // List — collect the contiguous run of list-item lines (any indent), then
    // build a nested tree from the indentation.
    if (isListLine(line)) {
      const buf: string[] = [];
      while (i < lines.length && isListLine(at(i))) {
        buf.push(at(i));
        i += 1;
      }
      out.push({ kind: "list", node: buildList(buf) });
      continue;
    }

    // Paragraph. Consume the opening line UNCONDITIONALLY (forward progress),
    // then keep joining following lines until a blank line or a line that
    // opens a new block.
    const buf: string[] = [at(i)];
    i += 1;
    while (
      i < lines.length &&
      at(i).trim() !== "" &&
      !at(i).startsWith("#") &&
      !at(i).trimStart().startsWith("```") &&
      !/^\s*>/.test(at(i)) &&
      !isListLine(at(i)) &&
      !(at(i).includes("|") && isTableSeparator(at(i + 1)))
    ) {
      buf.push(at(i));
      i += 1;
    }
    out.push({ kind: "p", text: buf.join(" ") });
  }

  return out;
}

/** Build a nested list tree from a run of list-item lines, using leading
 * indentation to decide nesting depth. */
function buildList(rawLines: string[]): ListNode {
  const parsed = rawLines.map((l) => {
    const m = l.match(LIST_RE);
    const indent = m?.[1]?.length ?? 0;
    const marker = m?.[2] ?? "-";
    const ordered = /\d+\./.test(marker);
    let text = m?.[3] ?? l.trim();
    let checked: boolean | null = null;
    const task = text.match(/^\[([ xX])\]\s+(.*)$/);
    if (task) {
      checked = task[1]?.toLowerCase() === "x";
      text = task[2] ?? "";
    }
    const startNum = ordered ? parseInt(marker, 10) || 1 : 1;
    return { indent, ordered, text, checked, startNum };
  });

  const first = parsed[0];
  const root: ListNode = { ordered: first?.ordered ?? false, start: first?.startNum ?? 1, items: [] };
  const stack: Array<{ indent: number; node: ListNode }> = [{ indent: first?.indent ?? 0, node: root }];

  for (const p of parsed) {
    while (stack.length > 1 && p.indent < (stack[stack.length - 1]?.indent ?? 0)) stack.pop();
    let top = stack[stack.length - 1]!;
    if (p.indent > top.indent) {
      const parentItem = top.node.items[top.node.items.length - 1];
      if (parentItem) {
        const child: ListNode = { ordered: p.ordered, start: p.startNum, items: [] };
        parentItem.children.push(child);
        stack.push({ indent: p.indent, node: child });
        top = stack[stack.length - 1]!;
      }
    }
    top.node.items.push({ text: p.text, checked: p.checked, children: [] });
  }
  return root;
}

function renderList(node: ListNode, key: number | string): ReactNode {
  const Tag = node.ordered ? "ol" : "ul";
  return (
    <Box
      key={key}
      component={Tag}
      {...(node.ordered && node.start !== 1 ? { start: node.start } : {})}
      sx={{ mt: 1.5, mb: 0, ml: 3, listStyleType: node.ordered ? "decimal" : "disc", fontSize: 14, lineHeight: 1.625 }}
    >
      {node.items.map((it, j) => (
        <Box
          key={j}
          component="li"
          sx={it.checked !== null ? { listStyleType: "none", ml: -2.5 } : {}}
        >
          {it.checked !== null && (
            <Box
              component="input"
              type="checkbox"
              checked={it.checked}
              readOnly
              sx={{ mr: 1, verticalAlign: "middle" }}
            />
          )}
          {inline(it.text)}
          {it.children.map((child, k) => renderList(child, `${j}-${k}`))}
        </Box>
      ))}
    </Box>
  );
}

const HEADING_SX: Record<number, object> = {
  1: { mt: 3, fontSize: "1.5rem", "&:first-of-type": { mt: 0 } },
  2: { mt: 2.5, fontSize: "1.125rem" },
  3: { mt: 2, fontSize: "1rem" },
  4: { mt: 2, fontSize: "0.9375rem" },
  5: { mt: 1.5, fontSize: "0.875rem" },
  6: { mt: 1.5, fontSize: "0.8125rem", color: "text.secondary" },
};

function renderBlock(b: Block, key: number): ReactNode {
  switch (b.kind) {
    case "h":
      return (
        <Box
          key={key}
          component={`h${b.level}` as "h1"}
          sx={{ fontWeight: 600, letterSpacing: "-0.01em", ...HEADING_SX[b.level] }}
        >
          {inline(b.text)}
        </Box>
      );
    case "p":
      return (
        <Box key={key} component="p" sx={{ mt: 1.5, fontSize: 14, lineHeight: 1.625, color: "text.primary" }}>
          {inline(b.text)}
        </Box>
      );
    case "code":
      return (
        <Box
          key={key}
          component="pre"
          sx={{ mt: 2, overflowX: "auto", borderRadius: 1, bgcolor: "surfaceSoft", px: 1.5, py: 1, fontSize: 12, fontFamily: "ui-monospace, monospace" }}
        >
          <code>{b.text}</code>
        </Box>
      );
    case "quote":
      return (
        <Box
          key={key}
          component="blockquote"
          sx={{ mt: 2, ml: 0, pl: 2, borderLeft: 3, borderColor: "divider", color: "text.secondary", "& > :first-of-type": { mt: 0 } }}
        >
          {parseBlocks(b.text).map((inner, i) => renderBlock(inner, i))}
        </Box>
      );
    case "list":
      return renderList(b.node, key);
    case "table":
      return (
        <Box key={key} sx={{ mt: 2, overflowX: "auto" }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <Box component="thead">
              <Box component="tr">
                {b.headers.map((h, j) => (
                  <Box
                    key={j}
                    component="th"
                    sx={{ border: 1, borderColor: "divider", bgcolor: "surfaceSoft", px: 1.5, py: 0.75, fontWeight: 600, textAlign: b.align[j] ?? "left" }}
                  >
                    {inline(h)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {b.rows.map((row, r) => (
                <Box component="tr" key={r}>
                  {b.headers.map((_, c) => (
                    <Box
                      key={c}
                      component="td"
                      sx={{ border: 1, borderColor: "divider", px: 1.5, py: 0.75, textAlign: b.align[c] ?? "left" }}
                    >
                      {inline(row[c] ?? "")}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      );
    case "hr":
      return <Box key={key} component="hr" sx={{ my: 3, border: 0, borderTop: 1, borderColor: "divider" }} />;
  }
}

/** Inline rules, tried in priority order; the earliest match in the string
 * wins. Content of formatting spans is rendered recursively (so **bold with a
 * [link](x)** works), except code/image whose content is literal. */
interface InlineRule {
  re: RegExp;
  render: (m: RegExpMatchArray, key: number) => ReactNode;
}

const INLINE_RULES: InlineRule[] = [
  {
    re: /!\[([^\]]*)\]\(([^)\s]+)\)/,
    render: (m, key) => (
      <Box
        key={key}
        component="img"
        src={m[2]}
        alt={m[1] ?? ""}
        sx={{ maxWidth: "100%", height: "auto", borderRadius: 1, my: 1, display: "block" }}
      />
    ),
  },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, key) => (
      <Box
        key={key}
        component="a"
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: "primary.main", textUnderlineOffset: "2px", "&:hover": { textDecoration: "underline" } }}
      >
        {inline(m[1] ?? "")}
      </Box>
    ),
  },
  {
    re: /`([^`]+)`/,
    render: (m, key) => (
      <Box
        key={key}
        component="code"
        sx={{ borderRadius: 0.5, bgcolor: (t) => alpha(t.palette.surfaceSoft, 1), px: 0.5, py: 0.25, fontFamily: "ui-monospace, monospace", fontSize: "12px" }}
      >
        {m[1]}
      </Box>
    ),
  },
  { re: /\*\*\*([^*]+)\*\*\*/, render: (m, key) => <strong key={key}><em>{inline(m[1] ?? "")}</em></strong> },
  { re: /\*\*([^*]+)\*\*/, render: (m, key) => <strong key={key}>{inline(m[1] ?? "")}</strong> },
  { re: /__([^_]+)__/, render: (m, key) => <strong key={key}>{inline(m[1] ?? "")}</strong> },
  { re: /~~([^~]+)~~/, render: (m, key) => <Box key={key} component="del">{inline(m[1] ?? "")}</Box> },
  { re: /\*([^*]+)\*/, render: (m, key) => <em key={key}>{inline(m[1] ?? "")}</em> },
  { re: /_([^_]+)_/, render: (m, key) => <em key={key}>{inline(m[1] ?? "")}</em> },
];

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;

  while (rest.length > 0) {
    let best: { rule: InlineRule; m: RegExpMatchArray; index: number } | null = null;
    for (const rule of INLINE_RULES) {
      const m = rest.match(rule.re);
      if (m && m.index !== undefined && (best === null || m.index < best.index)) {
        best = { rule, m, index: m.index };
        if (m.index === 0) break; // can't beat a match at the very start
      }
    }

    if (!best) {
      out.push(rest);
      break;
    }
    if (best.index > 0) out.push(rest.slice(0, best.index));
    out.push(best.rule.render(best.m, n++));
    rest = rest.slice(best.index + (best.m[0]?.length ?? 0));
  }

  return out;
}
